import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { CASE_STUDY_TEMPLATES, CaseStudySectionType, CONTENT_TYPES, ContentBlockType } from "@/types/case-study";
import {
  BookOpen,
  Target,
  Settings,
  Users,
  BarChart3,
  Type,
  AlertTriangle,
  Lightbulb,
  RotateCcw,
  Flag,
  X,
  Image,
  Grid3X3,
  Images,
  Minus,
  Quote,
  Info
} from "lucide-react";

interface SectionSelectorProps {
  open: boolean;
  onClose: () => void;
  onSelectSection: (sectionType: CaseStudySectionType) => void;
  onSelectElement?: (elementType: ContentBlockType) => void;
  position?: 'center' | 'above-button';
}

// Map icon names to their corresponding Lucide components
const iconMap = {
  BookOpen,
  Target,
  Settings,
  Users,
  BarChart3,
  Type,
  AlertTriangle,
  Lightbulb,
  RotateCcw,
  Flag,
  Image,
  Grid3X3,
  Images,
  Minus,
  Quote,
  Info,
} as const;

export const SectionSelector: React.FC<SectionSelectorProps> = ({
  open,
  onClose,
  onSelectSection,
  onSelectElement,
  position = 'center',
}) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setIsVisible(true);
    } else {
      // Delay hiding to allow exit animation
      const timer = setTimeout(() => setIsVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  if (!open && !isVisible) return null;

  // Custom modal for above-button positioning
  if (position === 'above-button') {
    return (
      <>
        {/* Backdrop */}
        <div 
          className={`fixed inset-0 transition-opacity duration-200 ${
            open ? 'bg-transparent' : 'bg-black/50 opacity-0'
          }`}
          onClick={onClose}
        />
        
        {/* Modal positioned above button */}
        <div className={`fixed bottom-20 right-6 z-[10000] bg-white rounded-lg shadow-xl border border-gray-200 w-96 max-h-[70vh] overflow-hidden transition-all duration-200 transform ${
          open ? 'animate-in fade-in-0 zoom-in-95 slide-in-from-bottom-2' : 'animate-out fade-out-0 zoom-out-95 slide-out-to-bottom-2'
        }`}>
          <div className="p-6 relative">
              <div className="grid grid-cols-1 gap-2 max-h-96 overflow-y-auto scroll-smooth"
                onScroll={(e) => {
                  const target = e.currentTarget;
                  const scrollCue = target.parentElement?.querySelector('.scroll-cue');
                  if (scrollCue) {
                    const isAtBottom = target.scrollHeight - target.scrollTop <= target.clientHeight + 10;
                    if (isAtBottom) {
                      scrollCue.classList.add('opacity-0');
                    } else {
                      scrollCue.classList.remove('opacity-0');
                    }
                  }
                }}
              >
                {CASE_STUDY_TEMPLATES.map((section) => (
                  <Button
                    key={section.type}
                    variant="ghost"
                    className="h-auto p-3 flex items-center justify-between text-left hover:bg-orange-50 hover:border-orange-500 transition text-gray-900 hover:text-gray-900 group border border-transparent"
                    onClick={() => {
                      onSelectSection(section.type);
                      onClose();
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8  rounded-full flex items-center justify-center">
                        {React.createElement(iconMap[section.icon as keyof typeof iconMap], {
                          className: "w-4 h-4 text-gray-600"
                        })}
                      </div>
                      <div className="flex flex-col">
                        <span className="font-medium text-gray-900 text-sm">{section.title}</span>
                        <p className="text-xs text-gray-600">{section.description}</p>
                      </div>
                    </div>
                    <div className="w-6 h-6 flex items-center justify-center">
                      <svg className="w-4 h-4 text-gray-400 group-hover:text-orange-500 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                  </Button>
                ))}
            </div>
            {/* Scroll cue gradient */}
            <div className="scroll-cue absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white via-white/80 to-transparent pointer-events-none transition-opacity duration-300"></div>
          </div>
        </div>
      </>
    );
  }

  // Default centered dialog
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[800px] rounded-lg bg-white p-10">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
        <DialogHeader>
          <DialogTitle>Add New Section</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="sections" className="w-full mt-4">
          <div className="bg-white p-2 rounded-xl shadow-[inset_0_2px_4px_rgba(0,0,0,0.05),inset_0_-2px_4px_rgba(0,0,0,0.05),inset_2px_0_4px_rgba(0,0,0,0.05),inset_-2px_0_4px_rgba(0,0,0,0.05)]">
            <TabsList className="h-auto bg-transparent p-0 justify-start w-full">
              <div className="flex items-center gap-1 w-full">
                <TabsTrigger
                  value="sections"
                  className="flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow-md data-[state=active]:border data-[state=active]:border-gray-200 data-[state=inactive]:text-[#737373] data-[state=inactive]:font-medium data-[state=inactive]:bg-transparent"
                >
                  Sections
                </TabsTrigger>
                <TabsTrigger
                  value="elements"
                  className="flex-1 px-3 py-1.5 text-sm font-medium rounded-md transition-all data-[state=active]:bg-white data-[state=active]:text-black data-[state=active]:shadow-md data-[state=active]:border data-[state=active]:border-gray-200 data-[state=inactive]:text-[#737373] data-[state=inactive]:font-medium data-[state=inactive]:bg-transparent"
                >
                  Elements
                </TabsTrigger>
              </div>
            </TabsList>
          </div>

          <TabsContent value="sections" className="mt-4">
            <div className="grid grid-cols-2 gap-4">
              {CASE_STUDY_TEMPLATES.map((section) => (
                <Button
                  key={section.type}
                  variant="outline"
                  className="h-auto p-4 flex justify-start items-start text-left hover:bg-orange-50 hover:border-orange-500 transition text-gray-900 hover:text-gray-900"
                  onClick={() => {
                    onSelectSection(section.type);
                    onClose();
                  }}
                >
                  <div className="flex">
                    {React.createElement(iconMap[section.icon as keyof typeof iconMap], {
                      className: "w-5 h-5 text-gray-600"
                    })}
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">{section.title}</span>
                    <p className="text-sm text-gray-600">{section.description}</p>
                  </div>
                </Button>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="elements" className="mt-4">
            <div className="grid grid-cols-2 gap-4">
              {CONTENT_TYPES.map((element) => (
                <Button
                  key={element.type}
                  variant="outline"
                  className="h-auto p-4 flex justify-start items-start text-left hover:bg-orange-50 hover:border-orange-500 transition text-gray-900 hover:text-gray-900"
                  onClick={() => {
                    onSelectElement?.(element.type);
                    onClose();
                  }}
                >
                  <div className="flex">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${element.color}`}>
                      {React.createElement(iconMap[element.icon as keyof typeof iconMap], {
                        className: "w-4 h-4"
                      })}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-900">{element.title}</span>
                    <p className="text-sm text-gray-600">{element.description}</p>
                  </div>
                </Button>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

